import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { ICONS } from '@/lib/gamify';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { Award, Lock, Trophy } from 'lucide-react';

const BadgeTile = ({ badge }) => {
  const { t, i18n } = useTranslation();
  const locale = (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2);
  const Icon = ICONS[badge.icon] || Award;
  const earned = badge.earned;
  const accent = earned ? (badge.accent || '#00B4D8') : undefined;
  const label = t(`gamify.badges.${badge.key}`, badge.key);
  const desc = t(`gamify.badgeDesc.${badge.key}`, '');
  const earnedOn = earned && badge.earned_at
    ? new Date(badge.earned_at).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`relative rounded-xl border p-3.5 flex flex-col items-center text-center gap-2 transition-colors ${
              earned
                ? 'bg-[#0A0A0A] border-white/12 hover:border-white/25'
                : 'bg-white/[0.02] border-white/8 opacity-60 hover:opacity-90'
            }`}
            data-testid={`achievement-${badge.key}`}
            data-earned={earned ? 'true' : 'false'}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={earned ? { background: `${accent}1f` } : { background: 'rgba(255,255,255,0.04)' }}
            >
              {earned
                ? <Icon className="w-6 h-6" style={{ color: accent }} />
                : <Lock className="w-5 h-5 text-muted-foreground" />}
            </div>
            <div className={`text-xs font-semibold leading-tight ${earned ? 'text-white' : 'text-muted-foreground'}`}>
              {label}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-[#12161B] border-white/15 text-white max-w-[220px]">
          <p className="font-semibold text-sm">{label}</p>
          {desc && <p className="text-xs text-muted-foreground mt-1">{desc}</p>}
          <p className="text-[11px] mt-1.5" style={{ color: earned ? '#2EE59D' : '#FFB020' }}>
            {earned
              ? (earnedOn ? t('profile.achievements.earnedOn', { date: earnedOn }) : t('profile.achievements.earned'))
              : t('profile.achievements.locked')}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const AchievementsCard = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get('/gamification/achievements');
        if (active) setData(data);
      } catch (e) {
        // Non-blocking: achievements are supplementary on the profile.
        if (active) setData({ badges: [], earned_count: 0, total: 0 });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  if (loading) {
    return <Skeleton className="h-48 w-full bg-white/10 rounded-2xl mt-6" />;
  }

  const badges = data?.badges || [];

  return (
    <div className="kite-card p-6 mt-6" data-testid="profile-achievements-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#FFB020]" /> {t('profile.achievements.title')}
        </h3>
        <span className="text-sm font-data text-muted-foreground" data-testid="achievements-count">
          {t('profile.achievements.earnedCount', { earned: data?.earned_count ?? 0, total: data?.total ?? 0 })}
        </span>
      </div>
      <p className="text-sm text-muted-foreground mb-4">{t('profile.achievements.subtitle')}</p>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3" data-testid="achievements-grid">
        {badges.map((b) => <BadgeTile key={b.key} badge={b} />)}
      </div>
    </div>
  );
};
